import { AppError } from "../core/errors";

export interface EncodeChoice {
  mime: string;
  extension: "mp4" | "webm";
  /** True when the container is MP4, which is what macOS plays natively. */
  native: boolean;
}

const CANDIDATES: EncodeChoice[] = [
  { mime: 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', extension: "mp4", native: true },
  { mime: 'video/mp4;codecs="avc1.640028,mp4a.40.2"', extension: "mp4", native: true },
  { mime: "video/mp4", extension: "mp4", native: true },
  { mime: 'video/webm;codecs="h264,opus"', extension: "webm", native: false },
  { mime: 'video/webm;codecs="vp9,opus"', extension: "webm", native: false },
  { mime: 'video/webm;codecs="vp8,opus"', extension: "webm", native: false },
];

export function negotiateFormat(): EncodeChoice {
  for (const candidate of CANDIDATES) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(candidate.mime)) {
      return candidate;
    }
  }
  throw new AppError(
    "unsupported",
    "This browser can’t record video. Chrome 126 or newer is required.",
  );
}

export interface EncoderOptions {
  videoBitsPerSecond: number;
  audioBitsPerSecond: number;
}

/**
 * MediaRecorder wrapper. Chunks are handed to the caller every second, which
 * is what lets the session write to disk continuously instead of holding a
 * whole take in memory and finalizing once at the end.
 */
export class Encoder {
  private recorder: MediaRecorder | null = null;
  private stopPromise: Promise<void> | null = null;
  private resolveStop: (() => void) | null = null;
  private rejectStop: ((error: Error) => void) | null = null;
  private failure: Error | null = null;
  private bytes = 0;
  private lastChunkAt = 0;
  private flushTimer = 0;
  chunkCount = 0;

  get msSinceChunk(): number {
    return this.lastChunkAt === 0 ? 0 : Date.now() - this.lastChunkAt;
  }

  get error(): Error | null {
    return this.failure;
  }

  start(
    stream: MediaStream,
    choice: EncodeChoice,
    options: EncoderOptions,
    onChunk: (chunk: Blob) => void,
    onError: (error: Error) => void,
  ): void {
    if (this.recorder) throw new Error("encoder already started");
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType: choice.mime,
        videoBitsPerSecond: options.videoBitsPerSecond,
        audioBitsPerSecond: options.audioBitsPerSecond,
      });
    } catch (error) {
      throw new AppError("encoder", "The encoder refused this stream.", String(error));
    }

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size === 0) return;
      this.bytes += event.data.size;
      this.chunkCount += 1;
      this.lastChunkAt = Date.now();
      onChunk(event.data);
    };
    recorder.onerror = (event: Event) => {
      const detail =
        "error" in event && event.error instanceof Error
          ? event.error.message
          : "MediaRecorder error";
      const error = new AppError("encoder", "The encoder stopped.", detail);
      this.failure = error;
      onError(error);
    };
    recorder.onstop = () => {
      this.recorder = null;
      if (this.failure) this.rejectStop?.(this.failure);
      else this.resolveStop?.();
      this.resolveStop = null;
      this.rejectStop = null;
    };

    const stopGate = Promise.withResolvers<void>();
    this.stopPromise = stopGate.promise;
    this.resolveStop = stopGate.resolve;
    this.rejectStop = stopGate.reject;

    try {
      recorder.start(1000);
    } catch (error) {
      this.recorder = null;
      throw new AppError("encoder", "The encoder could not start.", String(error));
    }
    this.recorder = recorder;
    this.lastChunkAt = Date.now();
    // The timeslice is a hint, not a promise: Chrome may hold chunks back
    // until a keyframe. Asking for the data keeps the journal within a second
    // of the encoder no matter how the muxer feels about it.
    this.flushTimer = window.setInterval(() => {
      const active = this.recorder;
      if (!active || active.state !== "recording") return;
      try {
        active.requestData();
      } catch {
        // A state change can race this tick; the next one is harmless.
      }
    }, 1000);
  }

  pause(): void {
    if (this.recorder?.state === "recording") this.recorder.pause();
  }

  resume(): void {
    if (this.recorder?.state === "paused") this.recorder.resume();
  }

  /** Stops and resolves once the last chunk has been delivered. */
  async stop(): Promise<void> {
    if (this.flushTimer) {
      window.clearInterval(this.flushTimer);
      this.flushTimer = 0;
    }
    const recorder = this.recorder;
    if (!recorder) return;
    if (recorder.state !== "inactive") {
      // Ask for the tail before stopping, then stop.
      try {
        recorder.requestData();
      } catch {
        // Some states refuse; the stop below still flushes.
      }
      recorder.stop();
    }
    if (this.stopPromise) {
      try {
        await this.stopPromise;
      } catch {
        // The failure was already surfaced through onError; keep the take.
      }
    }
  }
}
